/** The game's modal panels, the kind games keep (docs/ui-decisions.md): the
 *  Esc menu and the results screen. (The loading screen stays plain HTML:
 *  it must show before any script has run.) */

import { Show, createEffect, createSignal, type JSX } from "solid-js";
import { render } from "solid-js/web";

export interface GameMenu {
  resume(): void;
  settings(): void;
  main(): void;
}

export interface Results {
  title: string;
  line: string;
  /** Continue (the live world's next battle is already on), when there's a next. */
  next: boolean;
  /** Watch replay, when the battle was published. */
  replay: boolean;
  onContinue(): void;
  onReplay(): void;
  onMenu(): void;
}

const [gameMenu, setGameMenu] = createSignal<GameMenu | null>(null);
const [results, setResults] = createSignal<Results | null>(null);

export const openGameMenu = (m: GameMenu) => setGameMenu(() => m);
export const closeGameMenu = () => setGameMenu(null);
export const gameMenuOpen = () => gameMenu() !== null;
export const showResults = (r: Results) => setResults(() => r);
export const closeResults = () => setResults(null);

function Overlays(): JSX.Element {
  let resume: HTMLButtonElement | undefined;
  createEffect(() => gameMenu() && queueMicrotask(() => resume?.focus()));
  return (
    <>
      <Show when={gameMenu()}>
        {(m) => (
          <div class="game-menu" role="dialog" aria-label="Game menu">
            <div class="game-menu-panel">
              <button ref={resume} class="primary" onClick={() => m().resume()}>
                <kbd>Esc</kbd>Resume
              </button>
              <button onClick={() => m().settings()}>Settings</button>
              <button onClick={() => m().main()}>Main menu</button>
            </div>
          </div>
        )}
      </Show>
      <Show when={results()}>
        {(r) => (
          <div class="game-menu" role="dialog" aria-label="Battle over">
            <div class="game-menu-panel results-panel">
              <h1>{r().title}</h1>
              <p class="muted">{r().line}</p>
              <Show when={r().next}>
                <button class="primary" onClick={() => r().onContinue()}>
                  Continue
                </button>
              </Show>
              <Show when={r().replay}>
                <button onClick={() => r().onReplay()}>Watch replay</button>
              </Show>
              <button onClick={() => r().onMenu()}>Main menu</button>
            </div>
          </div>
        )}
      </Show>
    </>
  );
}

render(() => <Overlays />, document.getElementById("overlays")!);
